import type { BBox, LatLon } from '../../shared/types.js';
import { bboxAreaKm2 } from '../../shared/projection.js';
import { num, t } from '../i18n/i18n.js';

/**
 * What each step of drawing a race needs before the next one makes sense, said in words.
 *
 * The wizard never disables its Next button. A greyed-out button answers "can I go on?" with
 * no and refuses to say why, which is exactly the moment somebody needs telling — so instead
 * Next is always pressable, and pressing it too early turns the step's checklist red and says
 * what is missing and how to fix it. The same list is on screen the whole time, ticking itself
 * off, so the explanation is also available *before* anybody has pressed anything. See
 * DECISIONS.md §63.
 *
 * Pure, so the rules can be held to account by a unit test without a map or a browser.
 */

/** The race as drawn so far — what the picker knows, without Leaflet. */
export interface RaceDraft {
  bbox: BBox | null;
  /** A built-in region, if the box is still one. */
  fixture: string | null;
  start: LatLon | null;
  finish: LatLon | null;
  startBearing: number | null;
  gates: LatLon[];
  gateCount: number | null;
}

export interface Limits {
  maxAreaKm2: number;
  minAreaKm2: number;
  /** The server refuses Overpass, so only built-in regions can be raced. */
  offline: boolean;
}

/** What the map should draw attention to when a check fails. */
export type CheckTarget = 'box' | 'start' | 'finish' | 'gates' | 'search' | 'name' | 'list';

export interface Check {
  id: string;
  /** The requirement, phrased as something to do: "Place the finish". */
  label: string;
  ok: boolean;
  /** A warning is worth saying and not worth stopping for. Only `block` stops Next. */
  level: 'block' | 'warn';
  /** Why it is not met yet and what to do about it. Only read when `ok` is false. */
  problem: string;
  target?: CheckTarget;
}

/** Above this, Overpass is slow enough that somebody should be told before they wait. */
export const SLOW_AREA_KM2 = 2;
/** Closer than this, start and finish make a race that is over before it has started. */
export const SHORT_RACE_M = 150;
/** The most gates a host can place; the server refuses more. */
export const MAX_GATES = 12;

export function areaChecks(draft: RaceDraft, limits: Limits): Check[] {
  const area = draft.bbox ? bboxAreaKm2(draft.bbox) : 0;
  const checks: Check[] = [
    {
      id: 'box',
      label: t('Draw a box around the streets'),
      ok: draft.bbox !== null,
      level: 'block',
      problem: t(
        'There is no area yet. Drag across the map to draw a box around the streets you want to race, or press “Use current view” to take everything on screen.',
      ),
      target: 'box',
    },
  ];
  if (!draft.bbox) return checks;
  checks.push(
    {
      id: 'max',
      label: t('No bigger than {max} km²', { max: num(limits.maxAreaKm2) }),
      ok: area <= limits.maxAreaKm2,
      level: 'block',
      problem: t(
        'The box is {area} km² and the limit is {max} km², because a bigger city takes too long to download and build. Drag a corner inwards to shrink it.',
        { area: num(area, 2), max: num(limits.maxAreaKm2) },
      ),
      target: 'box',
    },
    {
      id: 'min',
      label: t('At least {min} km²', { min: num(limits.minAreaKm2) }),
      ok: area >= limits.minAreaKm2,
      level: 'block',
      problem: t(
        'The box is only {area} km², which is too small to fit a race. Drag a corner outwards to take in a few more streets.',
        { area: num(area, 2) },
      ),
      target: 'box',
    },
  );
  // A warning, not a refusal: an offline server still builds any area it has cached, and
  // only the dry run at the end can say whether this is one of them.
  if (limits.offline && !draft.fixture) {
    checks.push({
      id: 'offline',
      label: t('A server that can download streets'),
      ok: false,
      level: 'warn',
      problem: t(
        'This server is running offline, so a new area only works if somebody has raced it here before. The check at the end will say for certain; the built-in regions always work.',
      ),
      target: 'box',
    });
  }
  if (area > SLOW_AREA_KM2 && area <= limits.maxAreaKm2 && !draft.fixture) {
    checks.push({
      id: 'slow',
      label: t('A size that downloads quickly'),
      ok: false,
      level: 'warn',
      problem: t(
        'This is a big area, so the first download from OpenStreetMap can take a minute or two. You can carry on — it is only slow the first time.',
      ),
      target: 'box',
    });
  }
  return checks;
}

export function pinChecks(draft: RaceDraft): Check[] {
  const checks: Check[] = [
    {
      id: 'start',
      label: t('Place the start'),
      ok: draft.start !== null,
      level: 'block',
      problem: t('There is no start yet. Click the map where the race should begin.'),
      target: 'start',
    },
    {
      id: 'finish',
      label: t('Place the finish'),
      ok: draft.finish !== null,
      level: 'block',
      problem: draft.start
        ? t('There is no finish yet. Click the map where the race should end.')
        : t('There is no finish yet. Place the start first, then click again for the finish.'),
      target: 'finish',
    },
  ];
  const box = draft.bbox;
  if (box && draft.start && !inside(box, draft.start)) {
    checks.push({
      id: 'start-inside',
      label: t('Start inside the box'),
      ok: false,
      level: 'block',
      problem: t('The start is outside the box. Only the streets inside it are built, so drag the green pin back in.'),
      target: 'start',
    });
  }
  if (box && draft.finish && !inside(box, draft.finish)) {
    checks.push({
      id: 'finish-inside',
      label: t('Finish inside the box'),
      ok: false,
      level: 'block',
      problem: t('The finish is outside the box. Only the streets inside it are built, so drag the chequered pin back in.'),
      target: 'finish',
    });
  }
  if (draft.start && draft.finish) {
    const metres = distanceMetres(draft.start, draft.finish);
    if (metres < SHORT_RACE_M) {
      checks.push({
        id: 'short',
        label: t('Start and finish apart'),
        ok: false,
        level: 'warn',
        problem: t(
          'Start and finish are only {metres} m apart. If they land on the same street corner the race runs to the far side of the box instead.',
          { metres: Math.round(metres) },
        ),
        target: 'finish',
      });
    }
  }
  return checks;
}

export function routeChecks(draft: RaceDraft): Check[] {
  const box = draft.bbox;
  const outside = box ? draft.gates.findIndex((gate) => !inside(box, gate)) : -1;
  return [
    {
      id: 'gates-inside',
      label: t('Checkpoints inside the box'),
      ok: outside < 0,
      level: 'block',
      problem: t(
        'Checkpoint {n} is outside the box, where there are no streets to put it on. Drag it back in, or click it to remove it.',
        { n: outside + 1 },
      ),
      target: 'gates',
    },
  ];
}

/** The first check that stops Next, if any — what the error banner leads with. */
export function firstBlocker(checks: Check[]): Check | null {
  return checks.find((check) => !check.ok && check.level === 'block') ?? null;
}

export function inside(box: BBox, point: LatLon): boolean {
  return (
    point.lat >= box.south && point.lat <= box.north && point.lon >= box.west && point.lon <= box.east
  );
}

/** Equirectangular: exact enough across a few kilometres, which is all a box can be. */
export function distanceMetres(a: LatLon, b: LatLon): number {
  const R = 6_371_000;
  const lat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const dx = ((b.lon - a.lon) * Math.PI) / 180 * Math.cos(lat);
  const dy = ((b.lat - a.lat) * Math.PI) / 180;
  return Math.hypot(dx, dy) * R;
}
